import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ChevronDown, Expand, LoaderCircle, ChevronLeft,ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../../utils/GlobalState';
import { getAuthToken, decodedToken } from '../../utils/auth';
import { photomanagement } from "../../services/apiService";

const PhotoManagement = () => {
  const navigate = useNavigate();
  const { updateState } = useGlobalState();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("activeTab") || "Accepted";
  });
  const [currentPage, setCurrentPage] = useState(() => {
    return Number(localStorage.getItem("currentPage")) || 1;
  });

  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [imageLoading, setImageLoading] = useState({});
  const itemsPerPage = 12;
  const dropdownRefs = useRef([]);
  const { state } = useGlobalState();
  const { photoManagement } = state;
  const prevTabRef = useRef(activeTab);
  const token = getAuthToken();
  const user = decodedToken(token);
  const userType = user?.userType;

  const [localPhotos, setLocalPhotos] = useState(() => {
    const stored = localStorage.getItem('photoManagement');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        data: parsed.data?.filter((photo) => !photo.isDeleted) || [],
      };
    }
    return { data: [] };
  });

  useEffect(() => {
    if (photoManagement?.data?.length) {
      const nonDeletedPhotos = {
        ...photoManagement,
        data: photoManagement.data.filter((photo) => !photo.isDeleted),
      };
      localStorage.setItem("photoManagement", JSON.stringify(nonDeletedPhotos));
      setLocalPhotos(nonDeletedPhotos);
    } else if (photoManagement === null) {
    } else {
      const cached = localStorage.getItem("photoManagement");
      if (cached) {
        setLocalPhotos(JSON.parse(cached));
      }
    }
  }, [photoManagement]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRefs.current.every((ref) => ref && !ref.contains(event.target))) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
    if (prevTabRef.current !== activeTab) {
      setSelectedValues({});
      setSearchTerm("");
      setCurrentPage(1);
      localStorage.setItem("currentPage", "1");
    }

    prevTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem("currentPage", currentPage);
  }, [currentPage]);

  const handleNavigate = () => {
    navigate('/management');
  };

  const handleExpandClick = (photo) => {
    navigate('/photoreviewOBMExpert', { state: { photo } });
  };
  const handlePhotoManagementClick = async () => {
    try {
      setLoading(true);
      const data = await photomanagement();
      console.log("Photo Management API Response:", data);
      updateState({ photoManagement: data });
      navigate("/photomanagement");
    } catch (error) {
      console.error("Error in Photo Management click:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const allPhotos = (photoManagement?.data && photoManagement.data.length > 0)
    ? photoManagement.data.filter((photo) => !photo.isDeleted)
    : localPhotos?.data || [];
  const filteredPhotos = allPhotos.filter((item) => {
    const dataFromStatus = (item.dataFromStatus || "").toLowerCase().trim();
    const status = (item.status || "").toLowerCase().trim();

    if (["powerAdmin", "admin"].includes(userType)) {
      if (activeTab === "Accepted") {
        return status === "accepted";
      }
      if (activeTab === "Awaiting Acceptance") {
        return (
          (dataFromStatus === "detection_disagree" && status === "approved") ||
          (["detection_agree", "labeling"].includes(dataFromStatus) && status === "approved")
        );
      }
      if (activeTab === "Pending Products") {
        return status === "pending";
      }
      return false;
    }

    if (userType === "obmExpert") {
      if (activeTab === "Accepted") {
        return status === "accepted";
      }
      if (activeTab === "Awaiting Root Cause Review") {
        return (
          ["detection_agree", "labeling", "detection_disagree"].includes(dataFromStatus) &&
          status === "pending"
        );
      }
      return false;
    }
    return false;
  });
  const displayedPhotos = filteredPhotos.filter((p) => {
    let pass = true;
    if (selectedValues[0] && selectedValues[0] !== 'All') {
      const sel = selectedValues[0];
      const val = p.productInfo?.companyInfo || '';
      pass = pass && val === sel;
    }
    
    if (selectedValues[1] && selectedValues[1] !== 'All') {
      const sel = selectedValues[1];
      const val = p.productInfo?.productType || '';
      pass = pass && (sel === 'N/A' ? !val : val === sel);
    }
    if (selectedValues[2] && selectedValues[2] !== 'All') {
      const sel = selectedValues[2];
      const val =
        p.dataFromStatus?.toLowerCase() === 'detection_agree'
          ? [p.issueDetection?.predictions?.[0]?.issueType].filter(Boolean)
          : p.MuraRecording?.muraLabeling?.defects?.map((defect) => defect.muraType).filter(Boolean) || [];
      pass = pass && (sel === 'N/A' ? val.length === 0 : val.includes(sel));
    }

    if (selectedValues[3] && selectedValues[3] !== 'All') {
      const sel = selectedValues[3];
      const val =
        p.dataFromStatus?.toLowerCase() === 'detection_agree'
          ? [p.issueDetection?.predictions?.[0]?.rootCauseType].filter(Boolean)
          : p.MuraRecording?.muraLabeling?.defects?.map((defect) => defect.muraLabelIssue?.muraRootCause).filter(Boolean) || [];
      pass = pass && (sel === 'N/A' ? val.length === 0 : val.includes(sel));
    }

    if (selectedValues[4] && selectedValues[4] !== 'All') {
      const now = new Date();
      const periods = {
        'Within 1 month': 1,
        'Within 3 months': 3,
        'Within 6 months': 6,
        'Within 1 year': 12,
      };
      const months = periods[selectedValues[4]];
      if (months) {
        const threshold = new Date(now);
        threshold.setMonth(now.getMonth() - months);
        const uploadDate = new Date(p.updatedAt);
        pass = pass && uploadDate >= threshold;
      }
    }

    if (selectedValues[5] && selectedValues[5] !== 'All') {
      const mapping = {
        Labeling: 'labeling',
        'Detection: Agree': 'detection_agree',
        'Detection: Disagree': 'detection_disagree',
      };
      const target = mapping[selectedValues[5]];
      pass = pass && p.dataFromStatus?.toLowerCase() === target;
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      const normalize = (str) => (str ?? '').toString().replace(/_/g, ' ').toLowerCase().trim();

      const productType = normalize(p.productInfo?.productType);
      const brand = normalize(p.productInfo?.brand);
      const modelName = normalize(p.productInfo?.modelName);
      const dataFrom = normalize(p.dataFromStatus);

      const issueType = (
        p.dataFromStatus?.toLowerCase() === 'detection_agree'
          ? [p.issueDetection?.predictions?.[0]?.issueType ?? '']
          : p.MuraRecording?.muraLabeling?.defects?.map((defect) => defect.muraType ?? '') ?? []
      )
        .flat()
        .map(normalize)
        .join(', ');

      const rootCause = (
        p.dataFromStatus?.toLowerCase() === 'detection_agree'
          ? [p.issueDetection?.predictions?.[0]?.rootCauseType ?? '']
          : p.MuraRecording?.muraLabeling?.defects?.map((defect) => defect.muraLabelIssue?.muraRootCause ?? '') ?? []
      )
        .flat()
        .map(normalize)
        .join(', ');

      const uploadDateStr = p.updatedAt
        ? new Date(p.updatedAt).toLocaleDateString('en-IN').toLowerCase().trim()
        : '';

      pass = pass && (
        productType.includes(searchLower) ||
        issueType.includes(searchLower) ||
        rootCause.includes(searchLower) ||
        uploadDateStr.includes(searchLower) ||
        dataFrom.includes(searchLower) ||
        brand.includes(searchLower) ||
        modelName.includes(searchLower)
      );
    }

    return pass;
  });


  useEffect(() => {
    const newTotalPages = Math.ceil(displayedPhotos.length / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    } else if (currentPage > newTotalPages) {
      setCurrentPage(1);
    }
  }, [displayedPhotos.length, itemsPerPage]);

  const calculatePhotoCounts = () => {
    let acceptedCount = 0;
    let awaitingRootCauseCount = 0;
    let awaitingAcceptanceCount = 0;
    let filteredTotalCount = 0;

    allPhotos.forEach((item) => {
      const dataFromStatus = (item.dataFromStatus || "").toLowerCase().trim();
      const status = (item.status || "").toLowerCase().trim();

      if (status === "accepted") {
        acceptedCount++;
      }

      if (["powerAdmin", "admin"].includes(userType)) {
        if (
          status === "accepted" &&
          ["labeling", "detection_disagree", "detection_agree"].includes(dataFromStatus)
        ) {
          filteredTotalCount++;
        }
        if (
          (status === "approved" && ["labeling", "detection_agree", "detection_disagree"].includes(dataFromStatus))
        ) {
          awaitingAcceptanceCount++;
          filteredTotalCount++;
        }
      }

      if (userType === "obmExpert") {
        if (
          status === "accepted" &&
          ["labeling", "detection_disagree", "detection_agree"].includes(dataFromStatus)
        ) {
          filteredTotalCount++;
        }
        if (status === "pending" && ["detection_agree", "labeling", "detection_disagree"].includes(dataFromStatus)) {
          awaitingRootCauseCount++;
          filteredTotalCount++;
        }
      }
    });

    return {
      totalCount: filteredTotalCount,
      acceptedCount,
      awaitingRootCauseCount,
      awaitingAcceptanceCount,
    };
  };
  const now = new Date();

  const getUploadDateOptions = (photos) => {
    const options = new Set(['All']);

    photos.forEach((p) => {
      if (!p.updatedAt) return;

      const uploadDate = new Date(p.updatedAt);
      const diffDays = (now - uploadDate) / (1000 * 60 * 60 * 24);
      const diffMonths = diffDays / 30;

      if (diffDays <= 7) options.add('Within 1 week');
      if (diffMonths <= 1) options.add('Within 1 month');
      if (diffMonths <= 3) options.add('Within 3 months');
      if (diffMonths <= 6) options.add('Within 6 months');
      if (diffMonths <= 12) options.add('Within 1 year');
    });

    return options.size > 1 ? Array.from(options) : ['N/A'];
  };

  const mapping = {
    labeling: 'Labeling',
    detection_agree: 'Detection: Agree',
    detection_disagree: 'Detection: Disagree',
  };

  const getDataFromOptions = (photos) => {
    const options = new Set(['All']);

    photos.forEach((p) => {
      const status = p.dataFromStatus?.toLowerCase();
      if (status && mapping[status]) {
        options.add(mapping[status]);
      }
    });

    return options.size > 1 ? Array.from(options) : ['N/A'];
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPhotos = displayedPhotos.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayedPhotos.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const getUnique = (arr) => [...new Set(arr.filter(Boolean))];

  const productTypes = getUnique(filteredPhotos.map((p) => p.productInfo?.productType));
  const issueTypes = getUnique(
    filteredPhotos.flatMap((p) =>
      p.dataFromStatus?.toLowerCase() === "detection_agree"
        ? [p.issueDetection?.predictions?.[0]?.issueType].filter(Boolean)
        : p.MuraRecording?.muraLabeling?.defects?.map((defect) => defect.muraType).filter(Boolean) || []
    )
  );
  const rootCauses = getUnique(
    filteredPhotos.flatMap((p) =>
      p.dataFromStatus?.toLowerCase() === "detection_agree"
        ? [p.issueDetection?.predictions?.[0]?.rootCauseType].filter(Boolean)
        : p.MuraRecording?.muraLabeling?.defects?.map((defect) => defect.muraLabelIssue?.muraRootCause).filter(Boolean) || []
    )
  );
  const uploadOrgs = getUnique(
    filteredPhotos.map((p) => p.productInfo?.companyInfo).filter(Boolean)
  );
  const dropdowns = [
    {
      label: 'Upload Org',
      options: uploadOrgs.length ? ['All', ...uploadOrgs] : ['N/A'],
    },
    {
      label: 'Product Type',
      options: productTypes.length ? ['All', ...productTypes] : ['N/A'],
    },
    {
      label: 'Issue Type',
      options: issueTypes.length ? ['All', ...issueTypes] : ['N/A'],
    },
    {
      label: 'Issue Root Cause',
      options: rootCauses.length ? ['All', ...rootCauses] : ['N/A'],
    },
    { label: 'Upload Date', options: getUploadDateOptions(filteredPhotos) },
    { label: 'Data From', options: getDataFromOptions(filteredPhotos) },
  ];

  const handleSelect = (index, value) => {
    setSelectedValues((prev) => ({ ...prev, [index]: value }));
    setOpenDropdown(null);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const formatText = (text) => {
    if (!text) return 'N/A';
    return text
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div className="bg-black text-white min-h-screen flex flex-col mb-12">
      {/* STICKY TOP SECTION - NEVER SCROLLS */}
      <div className="sticky top-0 z-50 bg-black border-b border-slate-800 shadow-2xl">
        <div className="p-2 space-y-5">

          {/* Header */}
          <div className="flex items-center justify-between">
        <h1 className="text-lg sm:text-2xl font-bold flex items-center">
          <ArrowLeft className="mr-2 cursor-pointer" onClick={handleNavigate} />
          Photo Management
        </h1>
        <button
          onClick={handlePhotoManagementClick}
          disabled={loading}
          className="bg-blue-700 hover:bg-blue-600 text-white disabled:opacity-50 px-4 py-2 rounded-lg transition flex items-center gap-2"
        >
          {loading ? <LoaderCircle className="w-4 h-4 animate-spin" /> : "Refresh"}
        </button>
      </div>

      <p className="text-slate-400 text-sm">
        Prevent incorrect Accept/Reject decisions and provide a convenient option to review and download photos.
      </p>

      <input
        type="text"
        placeholder="Search photos"
        value={searchTerm}
        onChange={handleSearchChange}
        className="w-full p-2 bg-gray-800 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex flex-wrap gap-3 text-sm text-slate-400">
        {(() => {
          const { totalCount, acceptedCount, awaitingRootCauseCount, awaitingAcceptanceCount } =
            calculatePhotoCounts();

          if (userType === "obmExpert") {
            return (
              <>
                <span>Total {totalCount} Photos ,</span>
                <span>Accepted: {acceptedCount} ,</span>
                <span>Awaiting Root Cause Review: {awaitingRootCauseCount}</span>
              </>
            );
          }

          if (["powerAdmin", "admin"].includes(userType)) {
            return (
              <>
                <span>Total {totalCount} Photos ,</span>
                <span>Accepted: {acceptedCount} ,</span>
                <span>Awaiting Acceptance: {awaitingAcceptanceCount}</span>
              </>
            );
          }
          return (
            <>
              <span>Total {totalCount} Photos ,</span>
              <span>Accepted: {acceptedCount}</span>
            </>
          );
        })()}
      </div>

      <div className="flex md:space-x-4 border-b border-slate-600 overflow-x-auto">
        {['Accepted', 'Awaiting Root Cause Review', 'Awaiting Acceptance']
          .filter((tab) => {
            if (userType === 'obmExpert' && tab === 'Awaiting Acceptance') return false;
            if (['powerAdmin', 'admin'].includes(userType) && tab === 'Awaiting Root Cause Review') return false;
            return true;
          })
          .map((tab) => (
            <div key={tab} className="relative">
              <button
                className={`px-4 py-2 font-semibold rounded cursor-pointer whitespace-nowrap ${activeTab === tab ? 'text-white' : 'text-slate-400'
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-400"></div>
              )}
            </div>
          ))}
      </div>


      <div className="flex flex-col md:flex-row md:space-x-10 space-y-4 md:space-y-0 ">
        {dropdowns.map((dropdown, index) => (
          <div
            key={index}
            ref={(el) => (dropdownRefs.current[index] = el)}
            className="relative w-full md:w-80"
          >
            <button
              onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
              className="flex items-center justify-between bg-gray-800 p-2 rounded-full w-full text-white"
            >
               <span className="mx-auto text-left pl-2">
                    {selectedValues[index]
                      ? formatText(selectedValues[index])
                      : formatText(dropdown.label)}
                  </span>
              <ChevronDown
                className={`w-6 h-6 mr-2 text-gray-300 transition-transform duration-200 ${openDropdown === index ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {openDropdown === index && (
              <ul className="absolute left-1/2 -translate-x-1/2 w-60 bg-gray-900 top-full rounded-b-lg shadow-lg overflow-y-auto z-10 border border-white/20 border-t-0 max-h-60">
                {dropdown.options.length > 0 &&
                  dropdown.options[0] !== 'N/A' && (
                    <li className="p-2 bg-slate-700 text-gray-300 font-semibold text-center cursor-default">
                      {formatText(dropdown.label)}
                    </li>
                  )}

                {dropdown.options.map((option, idx) => (
                  <li
                    key={idx}
                    className={`p-2 text-white hover:bg-gray-600 cursor-pointer ${option === 'N/A' ? 'cursor-default opacity-70' : ''
                      }`}
                     onClick={() => {
                          if (option !== 'N/A') {
                            handleSelect(index, option);
                            setOpenDropdown(null); 
                          }
                        }
                        }
                  >
                    {formatText(option)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">  
        {currentPhotos.length > 0 ? (
          currentPhotos.map((photo, index) => {
            const dataFromStatus = photo.dataFromStatus?.toLowerCase() || '';
            let imageUrl = '';
            if (dataFromStatus === 'labeling' ||
              ((dataFromStatus === 'detection_disagree' || dataFromStatus === 'detection_agree') &&
                photo.MuraRecording?.rawImageUrl)) {
              imageUrl = photo.MuraRecording?.rawImageUrl || '';
            } else if (dataFromStatus === 'detection_disagree' || dataFromStatus === 'detection_agree') {
              imageUrl = photo.issueDetection?.originalImageUrl || '';
            }
            return (
              <div key={index} className="bg-black p-2 rounded-lg text-white">
                <div
                  className="relative w-full h-[30vh] sm:h-[40vh] md:h-[40vh] lg:h-[30vh] bg-gray-700 flex items-center justify-center cursor-pointer hover:bg-gray-600 transition border border-slate-500"
                  onClick={() => handleExpandClick(photo)}
                >
                  {imageUrl ? (
                    <>
                      {imageLoading[index] && (
                        <LoaderCircle className="w-8 h-8 text-white animate-spin absolute" />
                      )}
                      <img
                        src={imageUrl}
                        alt={`Uploaded ${photo._id}`}
                        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoading[index] ? 'opacity-0' : 'opacity-100'
                          }`}
                        onLoad={() => setImageLoading((prev) => ({ ...prev, [index]: false }))}
                        onError={() => setImageLoading((prev) => ({ ...prev, [index]: false }))}
                        ref={(img) => {
                          if (img && !imageLoading.hasOwnProperty(index)) {
                            setImageLoading((prev) => ({ ...prev, [index]: true }));
                          }
                        }}
                      />
                      {!imageLoading[index] && (
                        <div className="absolute inset-0 flex items-center justify-center font-semibold">
                          <Expand className="w-18 h-18 text-white font-semibold" />
                        </div>
                      )}
                    </>
                  ) : (
                    <Expand className="w-15 h-15 text-gray-300 font-semibold" />
                  )}
                </div>
                {photo.productInfo?.brand && (
                  <p className="whitespace-normal break-words">
                    <strong>Brand:</strong> {formatText(photo.productInfo.brand)}
                  </p>
                )}

                <div className="mt-2 space-y-2 min-w-0">
                  <p className="whitespace-normal break-words">
                    <strong>Model Name:</strong> {formatText(photo.productInfo?.modelName)}
                  </p>
                  <p className="whitespace-normal break-words">
                    <strong>Product Type:</strong> {formatText(photo.productInfo?.productType)}
                  </p>

                  <p className="whitespace-normal break-words">
                    <strong>Issue Type:</strong>{' '}
                    {photo.dataFromStatus?.toLowerCase() === 'detection_agree'
                      ? photo.MuraRecording?.muraLabeling?.defects?.some((defect) => defect.muraType)
                        ? [...new Set(
                          photo.MuraRecording?.muraLabeling?.defects
                            ?.map((defect) => formatText(defect.muraType))
                            .filter(Boolean)
                        )].join(', ') || 'N/A'
                        : [...new Set(
                          photo.issueDetection?.predictions?.map((pred) => formatText(pred.issueType)).filter(Boolean)
                        )].join(', ') || 'N/A'
                      : [...new Set(
                        photo.MuraRecording?.muraLabeling?.defects
                          ?.map((defect) => formatText(defect.muraType))
                          .filter(Boolean)
                      )].join(', ') || 'N/A'
                    }
                  </p>
                  <p className="whitespace-normal break-words">
                    <strong>Root Cause Type:</strong>{' '}
                    {photo.dataFromStatus?.toLowerCase() === 'detection_agree'
                      ? photo.MuraRecording?.muraLabeling?.defects?.some((defect) => defect.muraLabelIssue?.muraRootCause)
                        ? [...new Set(
                          photo.MuraRecording?.muraLabeling?.defects
                            ?.map((defect) => formatText(defect.muraLabelIssue?.muraRootCause))
                            .filter(Boolean)
                        )].join(', ') || 'N/A'
                        : [...new Set(
                          photo.issueDetection?.predictions?.map((pred) => formatText(pred.rootCauseType)).filter(Boolean)
                        )].join(', ') || 'N/A'
                      : [...new Set(
                        photo.MuraRecording?.muraLabeling?.defects
                          ?.map((defect) => formatText(defect.muraLabelIssue?.muraRootCause))
                          .filter(Boolean)
                      )].join(', ') || 'N/A'
                    }
                  </p>
                  {photo.dataFromStatus?.toLowerCase() === 'detection_agree'
                    ? photo.MuraRecording?.muraLabeling?.defects?.some((defect) => defect.muraLabelIssue?.muraRootIssue?.length > 0)
                      ? (
                        <p className="whitespace-normal break-words">
                          <strong>Issue Detail:</strong>{' '}
                          {[...new Set(
                            photo.MuraRecording?.muraLabeling?.defects
                              ?.flatMap((defect) => defect.muraLabelIssue?.muraRootIssue || [])
                              .map(formatText)
                              .filter(Boolean)
                          )].join(', ') || 'N/A'}
                        </p>
                      )
                      : null
                    : (
                      <p className="whitespace-normal break-words">
                        <strong>Issue Detail:</strong>{' '}
                        {[...new Set(
                          photo.MuraRecording?.muraLabeling?.defects
                            ?.flatMap((defect) => defect.muraLabelIssue?.muraRootIssue || [])
                            .map(formatText)
                            .filter(Boolean)
                        )].join(', ') || 'N/A'}
                      </p>
                    )
                  }
                  <p className="whitespace-normal break-words">
                    <strong>Upload Date:</strong>{' '}
                    {photo.updatedAt ? new Date(photo.updatedAt).toLocaleDateString('en-IN') : 'N/A'}
                  </p>
                  <p className="whitespace-normal break-words">
                    <strong>Upload Org:</strong> {formatText(photo.productInfo?.companyInfo)}
                  </p>
                  <p className="whitespace-normal break-words">
                    <strong>Uploaded by:</strong> {formatText(photo.email)}
                  </p>
                  <p className="whitespace-normal break-words">
                    <strong>Data From:</strong> {formatText(photo.dataFromStatus)}
                  </p>
                  {photo.status !== "approved" && (
                    <p className="whitespace-normal break-words">
                      <strong>State:</strong> {formatText(photo.status)}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-span-1 md:col-span-3 text-center py-10 text-gray-400">
            No data available
          </div>
        )}
      </div>

     <div className="fixed bottom-0 left-0 right-0 bg-gray-800/95 backdrop-blur-sm z-50  ">
      <div className="container mx-auto">
        <div className="flex items-center justify-center py-4 space-x-4">
          {/* Previous Button */}
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className={`border border-gray-700 rounded-md p-1 transition-all ${
              currentPage === 1 
                ? "opacity-50 cursor-not-allowed" 
                : "hover:bg-gray-700 hover:border-gray-600"
            }`}
          >
            <ChevronLeft className="h-5 w-5 text-gray-400" />
          </button>

          {/* Page Numbers */}
          <div className="flex space-x-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => paginate(pageNum)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    currentPage === pageNum
                      ? "bg-blue-900 text-blue-300 shadow-lg shadow-blue-900/50"
                      : "text-gray-400 hover:bg-gray-700 hover:text-white"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={nextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`border border-gray-700 rounded-md p-1 transition-all ${
              currentPage === totalPages || totalPages === 0
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-700 hover:border-gray-600"
            }`}
          >
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        </div>

      </div>
    </div>

    </div>
  );
};

export default PhotoManagement; 
